import { Permission } from 'src/app/interfaces/masterpass/policy';
import { PermissionMap } from './auth.state';

/**
 * Merges a user's organisation, department and role permissions into the
 * `{ [resource]: Permission }` map the whole app checks against.
 *
 * `responses` is positional and matches the order the requests were issued:
 * organisation first, then department (only if the user has one), then role (only
 * if the user has one). Any entry may be null, because each request catches its own
 * failure so that one dead endpoint does not sink the rest.
 *
 * Precedence: earlier sources win. A permissionId already contributed by the
 * organisation is not overwritten by the department or role.
 *
 * Only active permissions attached to a module are kept.
 */
export function mergePermissions(responses: any[]): PermissionMap {
  const merged: any[] = [];

  // Failed sources are skipped rather than ending the merge. The original walked
  // the array by index and tested the slot *before* the one it was about to read,
  // so a failed department request made it skip the role permissions too — and a
  // failed organisation request threw outright. Both contradicted the intent the
  // code documented: "if other endpoint fails, the one successfull still processed".
  for (const response of responses) {
    if (!response?.data) continue;

    const usable = response.data.filter((p: any) => p.active && p.module !== null);

    // Deduped against earlier sources only, never within a source — matching the
    // original, which appended a source's own entries wholesale.
    const ids = new Set(merged.map((d: Permission) => d.permissionId));
    merged.push(...usable.filter((d: Permission) => !ids.has(d.permissionId)));
  }

  const permissionObject: PermissionMap = {};
  merged.forEach((value: Permission) => {
    permissionObject[value.resource] = value;
  });
  return permissionObject;
}

/** True when every permission source failed, so the merge result means nothing. */
export function allSourcesFailed(responses: any[]): boolean {
  return responses.length > 0 && responses.every(response => !response?.data);
}
